import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import { marriageBanns, marriageCertificates } from '../../api/endpoints';
import { useFetchList } from '../../hooks';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge, SummaryNote
} from '../../components/ui';
import type { BadgeTone } from '../../components/ui';
import type { MarriageBann, MarriageCertificate } from '../../types';
import { marriageSummary, bannStatus, readingsDone } from './marriageAnalytics';

// The Marriage register's Summary tab. The register list itself is server-paged,
// so this reads the banns and certificate tables whole (their list endpoints
// return every row) and counts across all live entries, not one page.

const statusTone = (s: string): BadgeTone =>
  s === 'Approved' ? 'success' : (s === 'Objected' || s === 'Cancelled') ? 'error' : 'neutral';

const couple = (groom?: string, bride?: string) => `${groom || '—'} & ${bride || '—'}`;

const MarriageSummaryView = ({ isDarkMode }: { isDarkMode?: boolean }) => {
  const t = useStyles(isDarkMode).t;
  const banns = useFetchList<MarriageBann>((p, o) => marriageBanns.list(p, o));
  const certificates = useFetchList<MarriageCertificate>((p, o) => marriageCertificates.list(p, o));
  const loading = banns.loading || certificates.loading;

  const s = useMemo(() => marriageSummary(banns.data, certificates.data), [banns.data, certificates.data]);
  const year = new Date().getFullYear();
  const ministerPeak = peakOf(s.ministers, (r) => r.count);
  const monthPeak = peakOf(s.months, (r) => r.count);
  const yearPeak = peakOf(s.years, (r) => r.count);
  const bannCols = [
    { key: 'bannNo', header: 'Bann No.', nowrap: true, render: (r) => <strong>{r.bannNo || r.recordId || '-'}</strong> },
    { key: 'couple', header: 'Groom & Bride', render: (r) => couple(r.groomName || r.groomId, r.brideName || r.brideId) }
  ];

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Weddings Registered" value={String(s.certificates)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label={`Weddings ${year}`} value={String(s.weddingsThisYear)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label={`Weddings ${year - 1}`} value={String(s.weddingsLastYear)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Banns on File" value={String(s.banns)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Banns Pending" value={String(s.pending.length)} color={s.pending.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Readings (30 d)" value={String(s.readingsAhead.length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Awaiting Wedding" value={String(s.awaitingWedding.length)} color={s.awaitingWedding.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Objected / Cancelled" value={String(s.objected.length)} color={s.objected.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Inter-Church Banns" value={String(s.interChurch.length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Incomplete Certificates" value={String(s.incomplete.length)} color={s.incomplete.length ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Banns by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <Badge tone={statusTone(r.name)}>{r.name}</Badge> },
            {
              key: 'bar', header: 'Share',
              render: (r) => (
                <BarCell
                  value={percent(r.count, s.banns)} max={100} showPercent isDarkMode={isDarkMode}
                  color={r.name === 'Approved' ? t.income : r.name === 'Pending' ? t.warning : t.danger}
                />
              )
            },
            { key: 'count', header: 'Banns', align: 'right' }
          ]}
          rows={s.bannsByStatus}
          rowKey="name"
          emptyMessage="No banns on file yet."
          below={s.banns ? <TotalRow label="Total banns" value={s.banns} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Weddings by Minister"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Minister', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={ministerPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Weddings', align: 'right' }
          ]}
          rows={s.ministers}
          rowKey="name"
          emptyMessage="No weddings registered yet."
          below={s.certificates ? <TotalRow label="Total weddings" value={s.certificates} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Weddings — Last 12 Months"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.income} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Weddings', align: 'right' }
          ]}
          rows={s.months}
          rowKey="month"
          below={<TotalRow label="Last 12 months" value={s.months.reduce((n, m) => n + m.count, 0)} isDarkMode={isDarkMode} />}
        />
        <TablePanel
          title="Weddings by Year"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'year', header: 'Year', render: (r) => <strong>{r.year}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={yearPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Weddings', align: 'right' }
          ]}
          rows={s.years}
          rowKey="year"
          emptyMessage="No dated weddings yet."
          below={s.years.length ? <SummaryNote isDarkMode={isDarkMode}>The ten most recent years with weddings.</SummaryNote> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Banns Read — Awaiting the Wedding"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          ...bannCols,
          { key: 'thirdDate', header: 'Third Reading', nowrap: true, render: (r) => formatDate(r.thirdDate) },
          { key: 'approvalStatus', header: 'Status', render: (r) => <Badge tone={statusTone(bannStatus(r))}>{bannStatus(r)}</Badge> }
        ]}
        rows={s.awaitingWedding}
        rowKey={(r) => r.recordId || `${r.groomName}|${r.brideName}`}
        emptyMessage="Every couple whose banns were read three times has a marriage certificate."
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Pending Approval"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            ...bannCols,
            {
              key: 'readings', header: 'Read', align: 'center',
              render: (r) => {
                const done = readingsDone(r);
                return <Badge tone={done === 3 ? 'success' : done === 0 ? 'neutral' : 'warning'}>{done} / 3</Badge>;
              }
            }
          ]}
          rows={s.pending}
          rowKey={(r) => r.recordId || `${r.groomName}|${r.brideName}`}
          emptyMessage="No banns are waiting for approval."
        />
        <TablePanel
          title="Readings — Next 30 Days"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
            { key: 'reading', header: 'Reading', render: (r) => ['First', 'Second', 'Third'][r.reading - 1] },
            { key: 'couple', header: 'Groom & Bride', render: (r) => couple(r.bann.groomName || r.bann.groomId, r.bann.brideName || r.bann.brideId) }
          ]}
          rows={s.readingsAhead}
          rowKey={(r) => `${r.bann.recordId}|${r.reading}`}
          emptyMessage="No banns are due to be read in the next 30 days."
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Incomplete Certificates"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'recordId', header: 'Record', nowrap: true, render: (r) => <strong>{r.cert.recordId || '-'}</strong> },
            { key: 'couple', header: 'Groom & Bride', render: (r) => couple(r.cert.groomName, r.cert.brideName) },
            { key: 'date', header: 'Married', nowrap: true, render: (r) => formatDate(r.cert.marriageDate) },
            { key: 'missing', header: 'Missing', render: (r) => <Badge tone="error">{r.missing.join(', ')}</Badge> }
          ]}
          rows={s.incomplete}
          rowKey={(r) => r.cert.recordId || `${r.cert.groomName}|${r.cert.brideName}`}
          emptyMessage="Every certificate has its numbers and two witnesses."
        />
        <TablePanel
          title="Inter-Church Banns"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            ...bannCols,
            { key: 'churches', header: 'Churches', wrap: true, render: (r) => `${r.groomChurch} / ${r.brideChurch}` }
          ]}
          rows={s.interChurch}
          rowKey={(r) => r.recordId || `${r.groomName}|${r.brideName}`}
          emptyMessage="No banns between two different churches."
          below={s.outsideSpouse.length ? (
            <SummaryNote isDarkMode={isDarkMode}>
              {s.outsideSpouse.length} registered wedding{s.outsideSpouse.length === 1 ? ' has' : 's have'} a spouse not linked to a member of this parish.
            </SummaryNote>
          ) : null}
        />
      </SummaryColumns>
    </>
  );
};

export default MarriageSummaryView;
